import { useEffect, useMemo } from "react";
import { ValueOf } from "@claroty/utilities/types/ValueOf";
import { useLocation, useRouteMatch } from "react-router-dom";
import { useBreadcrumbsStore } from "~/store/breadcrumbs/useBreadcrumbsStore";
import { URLS } from "../../../const/postAuthConsts";
import { BreadcrumbsOptionsTypes } from "../../app/BreadCrumbs/BreadcrumbsTypes";
import { PAGE_NAMES } from "../../app/pageUtils/pageConsts";
import { getPageNameParts, getPageTitleByUrl } from "../../app/pageUtils/pageUtils";
import { TOGGLE_BUTTON_ICONS } from "../IconToggleButton/IconToggleButton";

type IconToggleLinkType = {
    text?: string;
    icon: ValueOf<typeof TOGGLE_BUTTON_ICONS>;
    link: string;
    isSelected?: boolean;
};

export type BreadcrumbsUpdaterProps = {
    showBreadcrumbs?: boolean;
    breadcrumbParams?: {
        lastPathName?: string;
    } & BreadcrumbsOptionsTypes;
    titleParam?: string;
    useLocationPath?: boolean;
};

function BreadcrumbsUpdater({
    showBreadcrumbs,
    breadcrumbParams,
    titleParam,
    useLocationPath,
}: BreadcrumbsUpdaterProps) {
    const setPathParts = useBreadcrumbsStore(s => s.setPathParts);
    const reset = useBreadcrumbsStore(s => s.reset);

    const match = useRouteMatch();
    const location = useLocation();

    const nameParts = useMemo(
        () => getPageNameParts(useLocationPath ? location.pathname : match.path),
        [match.path, location.pathname, useLocationPath],
    );

    useEffect(() => {
        if (!showBreadcrumbs) return;
        setBreadcrumbs(nameParts, setPathParts, breadcrumbParams);
        return () => reset();
    }, [nameParts, breadcrumbParams, showBreadcrumbs, setPathParts, reset]);

    // document.title
    useEffect(() => {
        document.title = getPageTitleByUrl(match.path, titleParam);
    }, [match.path, titleParam]);

    return null;
}

type PathPartsType = Array<{ text: string; link?: string }>;

function setBreadcrumbs(
    nameParts: PathPartsType,
    setPathParts: (pathParts: PathPartsType, options?: { iconsToggleLinks?: IconToggleLinkType[] }) => void,
    {
        lastPathName = undefined,
        iconsToggleLinks,
    }: {
        lastPathName?: string;
        iconsToggleLinks?: IconToggleLinkType[];
    } = {},
) {
    const breadcrumbs = [...nameParts];
    if (lastPathName) breadcrumbs.push({ text: lastPathName });
    breadcrumbs.unshift({ text: PAGE_NAMES[URLS.dashboard], link: URLS.dashboard });
    setPathParts(breadcrumbs, { iconsToggleLinks });
}

export default BreadcrumbsUpdater;
